import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/lib/settings";
import { Save } from "lucide-react";
import SortableSections from "@/components/SortableSections";

const TABS = [
  { key: "general", label: "General" },
  { key: "hero", label: "Homepage Hero" },
  { key: "metrics", label: "Homepage Metrics" },
  { key: "intro", label: "Homepage Intro" },
  { key: "why", label: "Why Choose Us" },
  { key: "homepage", label: "Homepage Sections" },
  { key: "contact", label: "Contact" },
  { key: "social", label: "Social Media" },
  { key: "portal", label: "Student Portal" },
  { key: "mobile_app", label: "Mobile App" },
  { key: "branding", label: "Branding" },
  { key: "chatbot", label: "Chatbot" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "seo", label: "SEO" },
  { key: "analytics", label: "Analytics" },
  { key: "footer", label: "Footer" },
];

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("general");

  useEffect(() => { api.get("/settings").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="p-8">Loading...</div>;

  const set = (path, value) => {
    const clone = JSON.parse(JSON.stringify(data));
    const parts = path.split(".");
    let cur = clone;
    for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
    cur[parts[parts.length - 1]] = value;
    setData(clone);
  };

  const save = async () => {
    try { await api.put("/settings", { data }); toast.success("Saved"); refresh(); }
    catch { toast.error("Save failed"); }
  };

  const F = ({ label, path, type = "text", rows }) => {
    const parts = path.split(".");
    let val = data;
    for (const p of parts) val = val?.[p] ?? "";
    const cls = "w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm";
    if (type === "textarea") return (
      <div><label className="text-xs font-semibold text-neutral-600">{label}</label>
        <textarea rows={rows || 3} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`settings-${path}`} />
      </div>
    );
    if (type === "checkbox") return (
      <label className="flex items-center gap-2"><input type="checkbox" checked={!!val} onChange={(e) => set(path, e.target.checked)} data-testid={`settings-${path}`} /><span className="text-sm">{label}</span></label>
    );
    return (
      <div><label className="text-xs font-semibold text-neutral-600">{label}</label>
        <input type={type} value={val} onChange={(e) => set(path, e.target.value)} className={cls} data-testid={`settings-${path}`} />
      </div>
    );
  };

  // Homepage section reorder + visibility
  const sections = data.homepage_sections || [];
  const moveSection = (idx, dir) => {
    const arr = [...sections];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    set("homepage_sections", arr);
  };
  const toggleSection = (idx) => {
    const arr = [...sections];
    arr[idx] = { ...arr[idx], visible: !arr[idx].visible };
    set("homepage_sections", arr);
  };

  // Homepage metrics (stats) editor
  const stats = data.stats || [];
  const setStat = (i, key, val) => { const arr = stats.map((x, idx) => idx === i ? { ...x, [key]: val } : x); set("stats", arr); };
  const addStat = () => set("stats", [...stats, { value: "", label: "" }]);
  const removeStat = (i) => set("stats", stats.filter((_, idx) => idx !== i));

  // Why Choose Us editor
  const whyItems = data.why_choose || [];
  const setWhy = (i, key, val) => { const arr = whyItems.map((x, idx) => idx === i ? { ...x, [key]: val } : x); set("why_choose", arr); };
  const addWhy = () => set("why_choose", [...whyItems, { title: "", desc: "" }]);
  const removeWhy = (i) => set("why_choose", whyItems.filter((_, idx) => idx !== i));

  // Quick replies for chatbot
  const setQuickReplies = (str) => set("chatbot_config.quick_replies", str.split(",").map((s) => s.trim()).filter(Boolean));

  return (
    <div className="p-8 pb-24 max-w-6xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="font-display font-black text-3xl" data-testid="admin-settings-title">Site Settings</h1>
        <button onClick={save} className="btn-primary-eq text-sm" data-testid="settings-save"><Save className="w-4 h-4" /> Save</button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-100"}`}
            data-testid={`settings-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl p-6 border border-neutral-200">
        {tab === "general" && (<div className="grid gap-4">
          <F label="Site name" path="site_name" />
          <F label="Tagline" path="tagline" />
          <F label="About text (homepage)" path="about_text" type="textarea" />
        </div>)}

        {tab === "hero" && (<div className="grid gap-4">
          <F label="Hero title" path="hero_title" type="textarea" rows={2} />
          <F label="Hero subtitle" path="hero_subtitle" type="textarea" />
          <F label="Primary CTA text" path="hero_cta_primary" />
          <F label="Primary CTA link" path="hero_cta_primary_link" />
        </div>)}

        {tab === "metrics" && (<div className="grid gap-4" data-testid="settings-metrics-panel">
          <p className="text-sm text-neutral-500">Edit the placement metrics shown in the dark stats band on the homepage. Values can be text like <b>90%</b>, <b>9/10</b> or <b>500+</b> — the counter animates the numeric part automatically.</p>
          {stats.map((st, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-3 items-end p-4 rounded-xl border border-neutral-200 bg-neutral-50" data-testid={`metric-row-${i}`}>
              <div><label className="text-xs font-semibold text-neutral-600">Value</label>
                <input value={st.value || ""} onChange={(e) => setStat(i, "value", e.target.value)} placeholder="90%" className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`metric-value-${i}`} />
              </div>
              <div><label className="text-xs font-semibold text-neutral-600">Label</label>
                <input value={st.label || ""} onChange={(e) => setStat(i, "label", e.target.value)} placeholder="Course completion rate" className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`metric-label-${i}`} />
              </div>
              <button onClick={() => removeStat(i)} className="px-3 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50" data-testid={`metric-remove-${i}`}>Remove</button>
            </div>
          ))}
          <button onClick={addStat} className="btn-outline-eq text-sm w-fit" data-testid="metric-add">+ Add metric</button>
        </div>)}

        {tab === "why" && (<div className="grid gap-4" data-testid="settings-why-panel">
          <p className="text-sm text-neutral-500">Edit the "Why Choose Us" feature cards shown in the homepage intro section.</p>
          {whyItems.map((w, i) => (
            <div key={i} className="grid gap-3 p-4 rounded-xl border border-neutral-200 bg-neutral-50" data-testid={`why-row-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-neutral-500">Card {i + 1}</span>
                <button onClick={() => removeWhy(i)} className="px-3 py-1 text-xs rounded-lg border border-red-200 text-red-600 hover:bg-red-50" data-testid={`why-remove-${i}`}>Remove</button>
              </div>
              <div><label className="text-xs font-semibold text-neutral-600">Title</label>
                <input value={w.title || ""} onChange={(e) => setWhy(i, "title", e.target.value)} className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`why-title-${i}`} />
              </div>
              <div><label className="text-xs font-semibold text-neutral-600">Description</label>
                <textarea rows={2} value={w.desc || ""} onChange={(e) => setWhy(i, "desc", e.target.value)} className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm" data-testid={`why-desc-${i}`} />
              </div>
            </div>
          ))}
          <button onClick={addWhy} className="btn-outline-eq text-sm w-fit" data-testid="why-add">+ Add card</button>
        </div>)}

        {tab === "intro" && (<div className="grid gap-4">
          <F label="Show intro section" path="home_intro.enabled" type="checkbox" />
          <F label="Kicker (small label above heading)" path="home_intro.kicker" />
          <F label="Heading" path="home_intro.heading" type="textarea" rows={2} />
          <F label="Body copy" path="home_intro.body" type="textarea" rows={5} />
          <F label="Image URL (right side)" path="home_intro.image" />
          <F label="Badge text (overlaid on image)" path="home_intro.badge_text" />
          <F label="Show floating badge" path="home_intro.badge_visible" type="checkbox" />
        </div>)}

        {tab === "homepage" && (<div>
          <p className="text-sm text-neutral-500 mb-4">Drag sections by their handle to reorder, or toggle the eye icon to hide. Content inside each section is edited on its own admin page.</p>
          <SortableSections sections={sections} onChange={(next) => set("homepage_sections", next)} />
        </div>)}

        {tab === "contact" && (<div className="grid gap-4">
          <F label="Phone" path="contact.phone" />
          <F label="Email" path="contact.email" />
          <F label="Address" path="contact.address" />
          <F label="WhatsApp number" path="contact.whatsapp" />
          <F label="Google Map URL" path="contact.map_url" />
          <F label="Working hours" path="contact.hours" />
        </div>)}

        {tab === "social" && (<div className="grid gap-4">
          <F label="Facebook" path="social.facebook" />
          <F label="Instagram" path="social.instagram" />
          <F label="LinkedIn" path="social.linkedin" />
          <F label="YouTube" path="social.youtube" />
          <F label="Twitter / X" path="social.twitter" />
        </div>)}

        {tab === "portal" && (<div className="grid gap-4">
          <F label="Student Login URL" path="student_portal.login" />
          <F label="Student Signup URL" path="student_portal.signup" />
          <F label="Courses portal URL" path="student_portal.courses" />
        </div>)}

        {tab === "mobile_app" && (<div className="grid gap-4">
          <F label="Enable mobile app section" path="mobile_app.enabled" type="checkbox" />
          <F label="Section heading" path="mobile_app.heading" />
          <F label="Subheading" path="mobile_app.subheading" type="textarea" />
          <F label="Google Play URL" path="mobile_app.google_play_url" />
          <F label="Apple App Store URL" path="mobile_app.apple_store_url" />
          <F label="Google Play badge image URL" path="mobile_app.google_play_badge" />
          <F label="App Store badge image URL" path="mobile_app.apple_store_badge" />
          <F label="App mockup image URL" path="mobile_app.mockup_image" />
          <F label="QR code image URL" path="mobile_app.qr_image" />
        </div>)}

        {tab === "branding" && (<div className="grid gap-4">
          <F label="Light-theme logo URL" path="branding.logo_light" />
          <F label="Dark-theme logo URL" path="branding.logo_dark" />
          <F label="Favicon URL" path="branding.favicon" />
          <F label="Apple touch icon URL" path="branding.apple_touch_icon" />
        </div>)}

        {tab === "chatbot" && (<div className="grid gap-4">
          <F label="Chatbot enabled" path="chatbot_config.enabled" type="checkbox" />
          <F label="Greeting message" path="chatbot_config.greeting" type="textarea" rows={2} />
          <F label="Avatar image URL" path="chatbot_config.avatar_url" />
          <F label="Primary color (hex)" path="chatbot_config.primary_color" />
          <div><label className="text-xs font-semibold text-neutral-600">Position</label>
            <select value={data.chatbot_config?.position || "bottom-right"} onChange={(e) => set("chatbot_config.position", e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm">
              <option value="bottom-right">Bottom right</option>
              <option value="bottom-left">Bottom left</option>
            </select>
          </div>
          <div><label className="text-xs font-semibold text-neutral-600">Quick replies (comma-separated)</label>
            <input value={(data.chatbot_config?.quick_replies || []).join(", ")} onChange={(e) => setQuickReplies(e.target.value)}
                   className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm"
                   placeholder="Show courses, Book demo, Fees, Placement" data-testid="settings-chatbot-quick-replies" />
          </div>
        </div>)}

        {tab === "whatsapp" && (<div className="grid gap-4">
          <F label="Business number (e.g. +919876543210)" path="whatsapp_auto.business_number" />
          <F label="Greeting message" path="whatsapp_auto.greeting" type="textarea" rows={2} />
          <F label="Working hours" path="whatsapp_auto.working_hours" />
          <F label="Offline message" path="whatsapp_auto.offline_message" type="textarea" />
          <div><label className="text-xs font-semibold text-neutral-600">Widget position</label>
            <select value={data.whatsapp_auto?.position || "bottom-left"} onChange={(e) => set("whatsapp_auto.position", e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm">
              <option value="bottom-left">Bottom left</option>
              <option value="bottom-right">Bottom right (near chatbot)</option>
            </select>
          </div>
          <F label="Auto-reply enabled" path="whatsapp_auto.auto_reply_enabled" type="checkbox" />
        </div>)}

        {tab === "seo" && (<div className="grid gap-4">
          <F label="Meta title" path="seo.meta_title" />
          <F label="Meta description" path="seo.meta_description" type="textarea" />
          <F label="Keywords" path="seo.keywords" />
          <F label="Open Graph / Twitter image URL" path="seo.og_image" />
          <F label="Twitter handle (e.g. @eqtechacademy)" path="seo.twitter_handle" />
          <F label="Emit EducationalOrganization + Course JSON-LD structured data" path="seo.structured_data_enabled" type="checkbox" />
          <div className="pt-4 border-t border-neutral-200">
            <div className="font-display font-bold text-sm mb-2">Search-engine auto-indexing (IndexNow)</div>
            <p className="text-xs text-neutral-500 mb-3">When you publish a new course or blog, we automatically ping Bing/Yandex/Seznam via IndexNow and Google's sitemap endpoint so indexing happens in hours instead of days.</p>
            <F label="Production site URL (used for ping URLs and JSON-LD)" path="seo.site_url" />
            <F label="Enable auto-ping on publish" path="seo.indexnow_enabled" type="checkbox" />
            <F label="IndexNow key (auto-generated on first run)" path="seo.indexnow_key" />
            <button
              onClick={async () => {
                try { await api.post("/admin/seo/ping", { paths: ["/", "/courses", "/blog", "/services"] }); toast.success("Ping queued"); }
                catch { toast.error("Ping failed"); }
              }}
              className="btn-outline-eq text-sm mt-3" data-testid="settings-seo-manual-ping">
              Ping search engines now
            </button>
          </div>
        </div>)}

        {tab === "analytics" && (<div className="grid gap-4">
          <F label="Google Analytics 4 ID (G-XXXXXXXXXX)" path="analytics.ga4_id" />
          <F label="Google Tag Manager ID (GTM-XXXXXX)" path="analytics.gtm_id" />
          <F label="Meta Pixel ID" path="analytics.meta_pixel_id" />
          <F label="LinkedIn Insight ID" path="analytics.linkedin_insight_id" />
          <F label="Microsoft Clarity ID" path="analytics.clarity_id" />
          <F label="Hotjar ID" path="analytics.hotjar_id" />
          <F label="Google Search Console verification token" path="analytics.search_console_verification" />
          <F label="Custom <head> scripts (raw HTML)" path="analytics.custom_head_scripts" type="textarea" rows={5} />
          <F label="Custom <body> scripts (raw HTML)" path="analytics.custom_body_scripts" type="textarea" rows={5} />
        </div>)}

        {tab === "footer" && (<div className="grid gap-4">
          <F label="Powered-by text" path="footer.powered_by_text" />
          <F label="Powered-by URL" path="footer.powered_by_url" />
          <F label="Copyright" path="footer.copyright" />
        </div>)}
      </div>
    </div>
  );
}
